import { Component } from '@theme/component';

/**
 * A minimal horizontal scroll-snap carousel with previous/next controls.
 * Used by blocks/browse-by-collection.liquid.
 *
 * @extends {Component<{track: HTMLElement, previous: HTMLElement, next: HTMLElement}>}
 */
class ScrollCarouselComponent extends Component {
  requiredRefs = ['track'];

  connectedCallback() {
    super.connectedCallback();
    this.refs.track.addEventListener('scroll', this.#updateArrowState, { passive: true });
    this.#updateArrowState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.track.removeEventListener('scroll', this.#updateArrowState);
  }

  previous() {
    this.#scrollToAdjacentItem(-1);
  }

  next() {
    this.#scrollToAdjacentItem(1);
  }

  /**
   * Measuring "one item's width + gap" and scrolling by that pixel amount drifts out of sync
   * with `justify-content: space-between` (gap varies with container width) and with scroll-snap
   * itself, leaving a sliver of the previous/next card showing.
   *
   * Compute each item's absolute scroll-left offset (bounding-rect delta from the track's own
   * edge, plus the track's current scrollLeft — robust regardless of what the items' offsetParent
   * happens to be) and scroll there directly, clamped to the track's real max scroll. The clamp is
   * what actually fixes the cut card on the *last* page: near the end of the list there isn't
   * enough scrollable distance left to bring the final item flush to the start edge, so anything
   * that tries to align it there (scrollIntoView included) leaves a sliver showing. Clamping to
   * `maxScrollLeft` instead lands the tail flush against the *end*, with no partial card either
   * side.
   *
   * @param {1 | -1} direction
   */
  #scrollToAdjacentItem(direction) {
    const track = this.refs.track;
    const items = [...track.children];
    if (!items.length) return;

    const trackLeft = track.getBoundingClientRect().left;
    const contentOffsets = items.map((item) => item.getBoundingClientRect().left - trackLeft + track.scrollLeft);

    const currentIndex = contentOffsets.findIndex((offset) => offset >= track.scrollLeft - 1);
    const targetIndex = Math.max(0, Math.min(items.length - 1, (currentIndex === -1 ? 0 : currentIndex) + direction));

    const maxScrollLeft = track.scrollWidth - track.clientWidth;
    const targetScrollLeft = Math.max(0, Math.min(contentOffsets[targetIndex], maxScrollLeft));

    track.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
  }

  #updateArrowState = () => {
    const track = this.refs.track;
    const maxScrollLeft = track.scrollWidth - track.clientWidth;

    this.refs.previous?.toggleAttribute('disabled', track.scrollLeft <= 0);
    this.refs.next?.toggleAttribute('disabled', track.scrollLeft >= maxScrollLeft - 1);
  };
}

if (!customElements.get('scroll-carousel-component')) {
  customElements.define('scroll-carousel-component', ScrollCarouselComponent);
}
